// src/utils/session.js
// 6 hour login session: when the access token expires the user is sent to /signin.

import axios from 'axios';

const DEFAULT_MESSAGE = 'Your session has expired. Please sign in again.';

const decodeTokenPayload = (token) => {
  try {
    const part = token.split('.')[1];
    const base64 = part.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    return JSON.parse(atob(padded));
  } catch (e) {
    return null;
  }
};

export const getTokenExpiryMs = (token = localStorage.getItem('access_token')) => {
  if (!token) return null;
  const payload = decodeTokenPayload(token);
  return payload && payload.exp ? payload.exp * 1000 : null;
};

export const isSessionExpired = () => {
  const expiry = getTokenExpiryMs();
  return expiry !== null && Date.now() >= expiry;
};

// Clears the login data (no redirect) and remembers a message for the sign in page
export const clearSession = (message = DEFAULT_MESSAGE) => {
  try {
    sessionStorage.setItem('session_message', message);
  } catch (e) {}
  localStorage.clear();
};

// Clears the login data and goes to the sign in page
export const endSession = (message = DEFAULT_MESSAGE) => {
  clearSession(message);
  if (window.location.pathname !== '/signin') {
    window.location.replace('/signin');
  }
};

const isLoginUrl = (url = '') => /\/login\/?(\?|$)/.test(String(url));
const isRefreshUrl = (url = '') => /\/(refresh|token)\/?(\?|$)/.test(String(url));

// Only requests to OUR backend get the login token
const envOrigins = [process.env.REACT_APP_BACKEND_URL, process.env.REACT_APP_API_URL]
  .filter(Boolean)
  .map((value) => {
    try {
      return new URL(value).origin;
    } catch (e) {
      return null;
    }
  })
  .filter(Boolean);

const API_ORIGINS = [
  'https://super-backend.hira-tech.co.uk',
  'https://super-backend.hira-tech.co.uk',
  'http://localhost:5566',
  ...envOrigins
];

const isApiUrl = (url = '') => {
  try {
    return API_ORIGINS.includes(new URL(String(url), window.location.origin).origin);
  } catch (e) {
    return false;
  }
};

const shouldAttachToken = (url) =>
  !!localStorage.getItem('access_token') &&
  localStorage.getItem('isAuthenticated') === 'true' &&
  isApiUrl(url) &&
  !isLoginUrl(url) &&
  !isRefreshUrl(url);
const isLoggedIn = () => localStorage.getItem('isAuthenticated') === 'true';

const isTokenProblem = (data) =>
  !!data && (data.code === 'token_not_valid' || /token/i.test(String(data.detail || data.error || '')));

let installed = false;

// Call once when the app starts
export const installSessionGuards = () => {
  if (installed) return;
  installed = true;

  // 1) Check the token expiry time regularly (also when the tab is opened again)
  const check = () => {
    if (isLoggedIn() && isSessionExpired()) endSession();
  };
  check();
  setInterval(check, 10000);
  window.addEventListener('focus', check);
  document.addEventListener('visibilitychange', check);

  // 2) Any page that uses fetch(): a token error from the server ends the session
  const originalFetch = window.fetch.bind(window);
  window.fetch = async (...args) => {
    const input = args[0];
    const url =
      typeof input === 'string' ? input : input instanceof URL ? input.href : (input && input.url) || '';

    // Pages that call the backend without a token now send the login token, so the
    // backend knows who is calling (shop isolation for ADMIN / STAFF).
    if ((typeof input === 'string' || input instanceof URL) && shouldAttachToken(url)) {
      const headers = new Headers((args[1] && args[1].headers) || {});
      if (!headers.has('Authorization')) {
        headers.set('Authorization', `Bearer ${localStorage.getItem('access_token')}`);
      }
      args = [input, { ...(args[1] || {}), headers }];
    }

    const response = await originalFetch(...args);
    if (response.status === 401 && isLoggedIn()) {
      if (!isLoginUrl(url)) {
        let data = null;
        try {
          data = await response.clone().json();
        } catch (e) {}
        if (isSessionExpired() || isTokenProblem(data)) endSession();
      }
    }
    return response;
  };

  // 3) Any page that uses the normal axios object
  axios.interceptors.request.use((config) => {
    const rawUrl = String((config && config.url) || '');
    const fullUrl = /^https?:/i.test(rawUrl) ? rawUrl : `${(config && config.baseURL) || ''}${rawUrl}`;
    if (config && config.headers && !config.headers.Authorization && shouldAttachToken(fullUrl)) {
      config.headers.Authorization = `Bearer ${localStorage.getItem('access_token')}`;
    }
    return config;
  });

  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      const status = error && error.response && error.response.status;
      if (status === 401 && isLoggedIn() && !isLoginUrl(error.config && error.config.url)) {
        if (isSessionExpired() || isTokenProblem(error.response.data)) endSession();
      }
      return Promise.reject(error);
    }
  );
};